<template>
  <SpPage title="予約一覧" storeSelectable :loading="detail.busy || busy">
    <template #header>
      <Button icon @click="newReservationDate(date)">
        <AddStrongIcon />
      </Button>
      <Button icon @click="goTo('alert')">
        <Badge :show="!!currentStore.counts.alert" :text="formatExceed(currentStore.counts.alert)">
          <BellIcon />
        </Badge>
      </Button>
    </template>
    <DateBar
      :date="date"
      :holidays="allHolidays"
      :businessDay="businessDay"
      :reserveCalendar="reserveCalendar"
      :busy="carendarBusy"
      @setDate="setDate"
      @beforeShow="setDate(date)"
      @changeDate="changeDate"
    />
    <MemoBoard v-if="hasMemo" />
    <div class="reserves">
      <div v-if="!busy" class="reservations-head">
        <div v-if="!isNoReservation" class="total">
          <span class="reservations">予約数</span>
          <span class="number">{{ group }}</span> 組 <span class="number">{{ peoples }}</span> 名
        </div>
        <Button icon class="memo" @click="$store.commit('memo/show', true)"><MemoIcon /></Button>
      </div>
      <div class="no-data" v-if="isNoReservation">
        <ListIcon class="no-data-icon" width="64" height="64" :frame="false" />
        <p>該当する予約はありません</p>
      </div>
      <template v-else>
        <div v-for="(item, index) in ascTimeReserves" :key="index" class="reserve">
          <div v-if="isTimeSeparator(item.reserveDatetime, index)" class="time-separator">
            {{ timeLabel(item.reserveDatetime) }}
          </div>
          <div class="contents" @click="goTo(`reserve/${item.id}`)">
            <div class="status">
              <AlertIcon v-if="hasAlert(item)" width="24" height="24" class="alert" />
              <div class="visit-status" :class="statusClassMap(item.status)">
                {{ statusMap(item.status) }}
                <select
                  v-if="![2, 3, 11, 12].includes(item.status)"
                  :value="item.status"
                  class="status-select"
                  @click.stop
                  @change="changeStatus(item, $event)"
                  :disabled="gnaviMaintenance(item)"
                >
                  <option v-for="status in [4, 10, 5, 6, 7, 8, 1]" :value="status" :key="status">
                    {{ statusMap(status) }}
                  </option>
                </select>
              </div>
              <ul class="tags" v-if="(item.tags && item.tags.length) || item.courseIds.length">
                <li v-for="tag in iconFilters(item.tags)" :key="tag.id" class="tag">
                  <TagIcon :id="tag.icon" class="icon" width="16" height="16" />
                </li>
                <li v-if="item.courseIds.length" class="courseTag tag">
                  <KnifeForkIcon class="icon" width="16" height="16" />
                </li>
              </ul>
            </div>
            <div class="rsv-info">
              <p class="names">
                <template v-if="isBlock(item.type)">ブロック</template>
                <template v-else>
                  <template v-if="item.adults && item.children">
                    <span class="people-suffix">大</span>{{ item.adults }}<span class="people-suffix">名,</span>
                    <span class="people-suffix">子</span>{{ item.children }}<span class="people-suffix">名</span>
                  </template>
                  <template v-else-if="item.adults || item.children">
                    {{ item.adults || item.children }}<span class="people-suffix">名</span>
                  </template>
                  <span>・</span>
                  <span class="three-point-reader"> {{ reserveName(item, true) }}</span>
                  <span class="name-suffix">様</span>
                </template>
              </p>
              <div v-if="hasVisitCount(item.customer) || !!item.customer.syncMemberType" class="params">
                <MemberLabel v-if="!!item.customer.syncMemberType" />
                <VisitCount v-if="hasVisitCount(item.customer)" :visitCount="item.customer.visitCount" />
              </div>
            </div>
            <div class="names-kana three-point-reader" v-if="kanaName(item.customer)">
              {{ kanaName(item.customer) }}
            </div>
            <div class="tables">
              <TableIcon class="icon" width="16" height="16" />
              <span class="table-labels three-point-reader">
                <template v-if="item.tableIds[0] === -1">未指定</template>
                <template v-else>{{ item.tableIds.map(id => tables.byId[id].name).join(',') }}</template>
              </span>
              <span class="table-count" v-if="item.tableIds.length > 1">全{{ item.tableIds.length }}</span>
            </div>
            <div class="tel" v-if="item.customer.tel1 || item.customer.tel2">
              <TelIcon class="icon" width="16" height="16" />
              <p class="three-point-reader">
                {{ item.customer.tel1 }}
                <span v-if="item.customer.tel1 && item.customer.tel2">/</span>
                {{ item.customer.tel2 }}
              </p>
            </div>
            <div v-if="item.customer.officeName" class="company">
              <CompanyIcon class="icon" width="16" height="16" />
              <p class="three-point-reader">{{ item.customer.officeName }}</p>
            </div>
            <div class="three-point-reader rsv-name" v-if="item.reserveName">
              ご予約名：<b>{{ item.reserveName }}</b> 様
            </div>
            <div class="settlement" v-if="item.settlement">{{ settlement(item) }}</div>
            <div v-if="item.customer.attributes.length > 0" class="others">
              <CustomerLabels
                class="labels"
                :good="attributesTypeMap(item.customer.attributes).normal"
                :bad="attributesTypeMap(item.customer.attributes).bad"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
    <MemoModal />
    <ReserveCancelModal @change="id => change(id, 1)" />
    <CancelMailModal @sendDone="change($event, 1)" />
    <ResrakuMessageModal
      v-if="resrakuMessageModal"
      @close="closeResrakuMessageModal"
      :isConfirmedResrakuReserve="isConfirmedResrakuReserve"
      isCancel
    />
  </SpPage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { isEqual } from 'lodash'
import { toHourClock, today } from '@/utils/date'
import { toTimeObject } from '@/utils/time'
import { reserveName, isOverlap, couponPoint, couponPointNote, isConfirmedResrakuReserve } from '@/utils/reserve'
import { formatExceed } from '@/utils/number'
import { isResrakuAlignment } from '@/utils/store'
import { attributesMap } from '@/resources/customer/attributesMap'
import { isInteractiveSyncType, isGnavi } from '@/resources/reservation/syncTypeMap'
import statusMap, { statusClassMap, checkStatus } from '@/resources/reservation/statusMap'
import settlementMap, { pointPayment } from '@/resources/reservation/settlementMap'
import { isBlock } from '@/resources/reservation/typeMap'
import VisitCount from '@/components/molecules/VisitCount'
import MemberLabel from '@/components/atoms/MemberLabel'
import Button from '@/components/atoms/Button'
import SpPage from '@/components/templates/SpPage'
import DateBar from '@/components/molecules/DateBar'
import CustomerLabels from '@/components/molecules/CustomerLabels'
import MemoBoard from '@/components/organisms/MemoBoard'
import MemoModal from '@/components/organisms/MemoModal'
import ReserveCancelModal from '@/components/organisms/ReserveCancelModal'
import CancelMailModal from '@/components/organisms/CancelMailModal'
import ResrakuMessageModal from '@/components/organisms/ResrakuMessageModal'
import Badge from '@/components/molecules/Badge'
import AlertIcon from '@/components/icons/AlertIcon'
import AddStrongIcon from '@/components/icons/addStrong'
import BellIcon from '@/components/icons/bell'
import ListIcon from '@/components/icons/ListIcon'
import MemoIcon from '@/components/icons/memo'
import TagIcon from '@/components/icons/TagIcon'
import KnifeForkIcon from '@/components/icons/knifeFork'
import TableIcon from '@/components/icons/table'
import TelIcon from '@/components/icons/TelIcon'
import CompanyIcon from '@/components/icons/company'

export default {
  name: 'ReserveList',
  components: {
    VisitCount,
    MemberLabel,
    Button,
    SpPage,
    DateBar,
    CustomerLabels,
    ListIcon,
    MemoBoard,
    MemoModal,
    ReserveCancelModal,
    CancelMailModal,
    ResrakuMessageModal,
    Badge,
    AlertIcon,
    AddStrongIcon,
    BellIcon,
    MemoIcon,
    TagIcon,
    KnifeForkIcon,
    TableIcon,
    TelIcon,
    CompanyIcon,
  },
  props: {
    /** 日付 YYYY-MM-DD */
    date: {
      type: String,
      default: today,
    },
  },
  data() {
    return {
      GNAVI_MAINTENANCE,
      resrakuMessageModal: false,
      isConfirmedResrakuReserve: {},
    }
  },
  computed: {
    ...mapState({
      busy: state => state.reserves.busy,
      reserves: state => state.reserves,
      tables: state => state.tables,
      currentStore: state => state.stores.current.store,
      detail: state => state.reserves.detail,
      businessDay: state => state.businessDay.list,
      carendarBusy: state => state.init.busy,
      reserveCalendar: state => state.init.reserveCalendar,
    }),
    ...mapGetters({
      allReserves: 'reserves/all',
      allHolidays: 'holidays/all',
      hasMemo: 'memo/hasMemo',
      ascTimeReserves: 'reserves/ascTimeReserves',
      overCapacityByReserveId: 'reserves/overCapacityById',
      peoples: 'reserves/peoples',
    }),
    reserve() {
      const reserve = this.reserves.edit.reservation
      return {
        ...reserve,
        utilizationTime: reserve.utilizationTime || this.defaultUtilizationTime,
        resetTime: reserve.resetTime !== null ? reserve.resetTime : this.currentStore.time.reset,
      }
    },
    group() {
      return this.ascTimeReserves.filter(o => !this.isBlock(o.type)).length
    },
    isNoReservation() {
      return !this.busy && !this.reserves.allIds.length
    },
    isResrakuAlignment() {
      return isResrakuAlignment(this.currentStore)
    },
  },
  watch: {
    $route() {
      this.fetch()
    },
  },
  mounted() {
    this.$store.dispatch('holidays/get')
    this.$store.dispatch('businessDay/get')
    this.$store.dispatch('tables/get', { readonly: true })
    this.fetch()
  },
  methods: {
    reserveName,
    formatExceed,
    isBlock,
    couponPoint,
    couponPointNote,
    fetch() {
      const { dispatch } = this.$store
      dispatch('memo/get', this.date)
      dispatch('reserves/get', { date: this.date, readonly: !this.allReserves.length })
      dispatch('stores/current/get')
    },
    goTo(target) {
      this.$router.push({ path: `/sp/${target}` })
    },
    newReservationDate(date) {
      this.$router.push({ path: '/sp/reserve/new', query: { date } })
    },
    changeDate(date) {
      this.$router.push({ path: '/sp', query: { date } })
    },
    timeLabel(reserveTime) {
      return toHourClock(reserveTime).time
    },
    isTimeSeparator(time, index) {
      if (index === 0) return true

      // DynamicScollerのcacheが渡ってくる可能性があるためチェックを掛ける必要がある
      if (!this.ascTimeReserves[index - 1]) return false

      const prevReserveTime = toTimeObject(this.ascTimeReserves[index - 1].reserveDatetime)
      const reserveTime = toTimeObject(time)
      return !isEqual(prevReserveTime, reserveTime)
    },
    statusMap(status) {
      return statusMap[status]
    },
    statusClassMap(status) {
      return statusClassMap[status]
    },
    attributesTypeMap(attr) {
      const mapObj = {}
      attr.forEach(id => {
        const type = attributesMap[id].type
        mapObj[type] = mapObj[type] || []
        mapObj[type].push(attributesMap[id].label)
      })
      return mapObj
    },
    iconFilters(tags) {
      return tags.filter(tag => tag.icon)
    },
    hasVisitCount(customer) {
      return !!customer.id && customer.visitCount !== null
    },
    kanaName(item) {
      const { firstName, lastName, firstNameKana, lastNameKana } = item
      return (lastName || firstName) && (lastNameKana || firstNameKana)
        ? `${lastNameKana || ''} ${firstNameKana || ''}`
        : ''
    },
    settlement(reserve) {
      const { settlement } = reserve
      return `決済情報：${settlementMap[settlement]}${
        pointPayment(settlement) ? ` ${couponPoint(reserve)}${couponPointNote(reserve)}` : ''
      }`
    },
    async changeStatus(item, event) {
      const visitStatus = Number(event.target.value)
      if (checkStatus.isCanceled(visitStatus)) {
        this.showCancelModal(item.id)
      } else if (await this.$store.dispatch('reserves/detail/changeStatus', { id: item.id, visitStatus })) {
        this.change(item.id, visitStatus)
      }
    },
    async showCancelModal(id) {
      const { dispatch, commit } = this.$store
      const reserve = await dispatch('reserves/detail/get', { id })
      if (!reserve) return
      const { reserveDatetime, syncRsvId, syncType } = reserve
      this.isConfirmedResrakuReserve = isConfirmedResrakuReserve(reserve)
      if (this.isConfirmedResrakuReserve.confirmed && this.isResrakuAlignment) {
        // レスラク連携予約確定後のキャンセル
        this.resrakuMessageModal = true
      } else if (
        syncRsvId === null ||
        !isInteractiveSyncType(syncType) ||
        !this.currentStore.syncStatus.gnavi ||
        toHourClock(reserveDatetime).date < today()
      ) {
        commit('reserves/cancel/show', id)
      } else {
        commit('reserves/cancelMail/show', id)
      }
    },
    closeResrakuMessageModal() {
      this.resrakuMessageModal = false
      this.isConfirmedResrakuReserve = {}
    },
    change(reservationId, statusId) {
      this.$store.commit('reserves/updateReservationStatus', { reservationId, statusId })
    },
    hasAlert(item) {
      // 人数オーバー、重複予約、テーブル未指定
      return (
        !!this.overCapacityByReserveId[item.id] ||
        this.ascTimeReserves.some(rsv => isOverlap(rsv, item)) ||
        item.tableIds.includes(-1)
      )
    },
    gnaviMaintenance(reserve) {
      return GNAVI_MAINTENANCE && isGnavi(reserve.syncType)
    },
    async setDate(date) {
      await this.$store.dispatch('init/createCalendar', { date })
    },
  },
}
</script>

<style lang="postcss" scoped>
.layout {
  position: relative;
}

.reserves {
  height: 100%;
}

.reservations-head {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  margin: 16px;
}

.memo-board + .reserves > .reservations-head {
  margin-top: 0;
}

.no-data {
  position: absolute;
  text-align: center;
  top: 36%;
  width: 100%;
}

.no-data-icon {
  fill: var(--light-gray);
}

.total {
  align-items: end;
  display: grid;
  font-size: 13px;
  font-weight: bold;
  grid-template-columns: repeat(5, max-content);
  line-height: 0.6;

  .reservations {
    font-weight: normal;
  }
  .number {
    font-size: 20px;
  }
}

.memo {
  justify-self: flex-end;
}

.reserve {
  border-top: solid 1px var(--border-color);
}

.time-separator {
  background: var(--very-light-gray-2);
  box-sizing: border-box;
  font-weight: 600;
  height: 32px;
  line-height: 0.9;
  padding: 10px 16px;
}

.contents {
  background: var(--white);
  cursor: pointer;
  padding: 16px;
}

.icon {
  fill: var(--very-dark-gray-2);
}

.visit-status {
  box-sizing: border-box;
  color: var(--white);
  font-size: 12px;
  font-weight: bold;
  height: 24px;
  line-height: 1;
  padding: 6px 8px;
  position: relative;
  width: 104px;

  .status-select {
    appearance: none;
    background: transparent;
    border: none;
    color: transparent;
    height: 40px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 120px;

    &:focus {
      outline: none;
    }
  }

  &::after {
    border-color: currentColor transparent transparent;
    border-style: solid;
    border-width: 5px 5px 0;
    content: '';
    height: 0;
    pointer-events: none;
    position: absolute;
    right: 10px;
    top: 42.5%;
    width: 0;
  }

  &.table-unspecified,
  &.checking {
    border: solid 1px var(--pure-red);
    color: var(--pure-red);
    padding: 5px 8px;
    text-align: center;

    &::after {
      content: none;
    }
  }

  &.table-unsettled {
    background-color: var(--pure-red);
    text-align: center;

    &::after {
      content: none;
    }
  }

  &.visit-waiting {
    border: solid 1px var(--moderate-blue);
    color: var(--moderate-blue);
    padding: 5px 8px;
  }

  &.partial-visiting {
    background-color: var(--light-blue-2);
  }

  &.visiting {
    background-color: var(--moderate-blue);
    border-color: var(--moderate-blue);
  }

  &.visit {
    background-color: var(--moderate-blue);
  }

  &.eaten {
    background-color: var(--moderate-purple);
  }

  &.accounted,
  &.cleaned {
    background-color: var(--light-green);
  }

  &.return {
    background-color: var(--moderate-green);
  }

  &.block {
    background-color: var(--light-black);
    text-align: center;

    &::after {
      content: none;
    }
  }
}

.status {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: max-content 1fr;
  width: 100%;
}

.alert {
  fill: var(--pure-red);
  margin-right: 8px;
}

.tags {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 0 5px;
  justify-self: flex-end;

  .icon {
    display: block;
  }
}

.tag {
  align-items: center;
  height: 16px;
}

.tag + .courseTag {
  border-left: solid 1px var(--very-dark-gray);
  margin-left: 3px;
  padding-left: 8px;
}

.rsv-info {
  display: grid;
  grid-auto-flow: column;
  justify-self: flex-start;
  margin-top: 12px;
  width: 100%;
}

.names {
  align-items: baseline;
  display: grid;
  font-size: 16px;
  font-weight: bold;
  grid-auto-flow: column;
  justify-content: flex-start;
  margin-right: 8px;

  > span:last-of-type {
    margin-left: 4px;
  }
}

.people-suffix {
  font-size: 13px;
}

.name-suffix {
  font-size: 13px;
  font-weight: normal;
}

.params {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-end;
}

.names-kana {
  font-size: 11px;
}

.member-label {
  line-height: 1.5em;
}

.visit-count {
  font-size: 12px;
}

.tables {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 16px 1fr;
  margin-top: 4px;
}

.table-labels {
  margin: 0 6px 0 8px;
}

.table-count {
  border: 1px solid var(--dark-gray-1);
  box-sizing: border-box;
  color: var(--very-dark-gray-2);
  font-size: 10px;
  height: 16px;
  line-height: 1.4;
  padding: 0 4px;
}

.tel {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 0 8px;
  grid-template-columns: 16px 1fr;
}

.company {
  align-items: center;
  display: grid;
  grid-auto-columns: 1fr;
  grid-gap: 0 8px;
  grid-template-columns: 16px 1fr;
}

.rsv-name {
  font-size: 13px;
  margin-top: 4px;
}

.settlement {
  font-size: 13px;
  margin-top: 4px;
}

.three-point-reader {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attributes {
  color: var(--white);
  display: flex;
  flex-wrap: wrap;
  text-align: center;

  > li {
    font-size: 13px;
    line-height: 1.5;
    margin-top: 4px;
    padding: 0 4px;

    &:not(:last-of-type) {
      margin-right: 8px;
    }

    &.normal {
      background: var(--pure-orange);
    }
    &.bad {
      background: var(--pure-red);
    }
  }
}

.others {
  border-top: solid 1px var(--light-gray);
  margin-top: 10px;
  padding-top: 12px;
}
</style>
